import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  api,
  type ModelRow,
  type ProviderCatalogResponse,
  type ProviderDefinitionDto,
  type ProviderView,
  type RoutingDecisionResponse,
} from "../lib/api";
import {
  capChips,
  filterDefinition,
  filterProviderView,
  isNoKey,
  matchesQuery,
  needsAttention,
  sortDefinitions,
  sortProviderViews,
  summarizeFreePool,
  type ProviderFilter,
  type ProviderSort,
} from "../lib/provider-display";
import {
  Drawer,
  EmptyState,
  ErrorState,
  FilterBar,
  Metric,
  PageHeader,
  SectionHeader,
} from "../components/ui";
import { ConnectedRow, CatalogTile } from "./providers/cards";
import {
  CustomConnect,
  ManagedConnect,
  ProviderDetailDrawer,
} from "./providers/drawers";

type DrawerMode =
  | { type: "closed" }
  | { type: "managed"; def: ProviderDefinitionDto }
  | { type: "custom" }
  | { type: "detail"; provider: ProviderView };

const FILTERS: Array<{ id: ProviderFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "connected", label: "Connected" },
  { id: "free", label: "Free" },
  { id: "nokey", label: "No Key" },
  { id: "attention", label: "Needs Attention" },
  { id: "byok", label: "Paid / BYOK" },
];

const SORTS: Array<{ id: ProviderSort; label: string }> = [
  { id: "recommended", label: "Recommended" },
  { id: "health", label: "Health" },
  { id: "freemodels", label: "Free Models" },
  { id: "latency", label: "Latency" },
  { id: "recent", label: "Recently Added" },
];

export default function ProvidersPage() {
  const [catalog, setCatalog] = useState<ProviderCatalogResponse | null>(null);
  const [providers, setProviders] = useState<ProviderView[]>([]);
  const [freeModels, setFreeModels] = useState<ModelRow[]>([]);
  const [decision, setDecision] = useState<RoutingDecisionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ProviderFilter>("all");
  const [sort, setSort] = useState<ProviderSort>("recommended");
  const [drawer, setDrawer] = useState<DrawerMode>({ type: "closed" });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  async function load() {
    try {
      const [c, p, m] = await Promise.all([
        api<ProviderCatalogResponse>("/api/providers/catalog"),
        api<{ data: ProviderView[] }>("/api/providers"),
        api<{ data: ModelRow[] }>("/api/models?filter=free"),
      ]);
      setCatalog(c);
      setProviders(p.data);
      setFreeModels(m.data);
      setError(null);
      // Best-effort: what would NEXUS pick for a free request right now?
      api<RoutingDecisionResponse>("/api/router/simulate", {
        method: "POST",
        body: JSON.stringify({
          model: "free-auto",
          messages: [{ role: "user", content: "ping" }],
        }),
      })
        .then(setDecision)
        .catch(() => setDecision(null));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load providers");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Deep link: /providers?id=<providerId> opens the detail drawer.
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get("id");
  useEffect(() => {
    if (!idParam || drawer.type !== "closed" || providers.length === 0) return;
    const target = providers.find((p) => p.id === idParam || p.providerTypeId === idParam);
    if (target) setDrawer({ type: "detail", provider: target });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam, providers]);

  /* ── derived data ──────────────────────────────────────────── */

  const isDemo = (p: ProviderView) =>
    p.kind === "demo" || p.providerTypeId.startsWith("mock");

  const allConnected = providers.filter((p) => p.connectionStatus !== "not_connected");
  const connected = allConnected.filter((p) => !isDemo(p));
  const demoProviders = allConnected.filter(isDemo);
  const connectedTypeIds = new Set(allConnected.map((p) => p.providerTypeId));

  const availableDefs = useMemo(() => {
    const list = [...(catalog?.managed ?? []), ...(catalog?.anonymous ?? [])];
    const unique = new Map(list.map((d) => [d.id, d]));
    let defs = [...unique.values()].filter((d) => !connectedTypeIds.has(d.id));
    defs = defs.filter((d) => filterDefinition(d, filter));
    defs = defs.filter((d) => matchesQuery(d, query));
    return sortDefinitions(defs, sort);
  }, [catalog, connectedTypeIds, filter, query, sort]);

  const visibleConnected = useMemo(() => {
    let list = connected.filter((p) => filterProviderView(p, filter));
    list = list.filter((p) =>
      matchesQuery(
        {
          name: p.name,
          shortDescription: p.shortDescription,
          description: p.description,
          capabilities: p.definition.capabilities,
          features: p.definition.features,
          freeAccess: p.definition.freeAccess,
          category: p.definition.category,
        },
        query,
      ),
    );
    return sortProviderViews(list, sort);
  }, [connected, filter, query, sort]);

  const demoVisible = useMemo(
    () =>
      demoProviders.filter(
        (p) => matchesQuery({ name: p.name, shortDescription: p.shortDescription }, query) && filterProviderView(p, filter),
      ),
    [demoProviders, query, filter],
  );

  /* ── summary metrics ───────────────────────────────────────── */

  const healthyCount = connected.filter((p) =>
    ["healthy", "connected"].includes(p.connectionStatus),
  ).length;
  const attentionCount = connected.filter(needsAttention).length;
  const noKeySources =
    connected.filter((p) => p.connectionMode === "anonymous").length +
    (catalog?.anonymous ?? []).filter((d) => !connectedTypeIds.has(d.id)).length;
  const pool = useMemo(
    () => summarizeFreePool(connected, freeModels),
    [connected, freeModels],
  );

  const preferredRow = decision?.providerId
    ? providers.find((p) => p.id === decision.providerId)
    : null;
  const preferred = decision?.explanation?.selected?.providerDisplayName
    ? decision.explanation.selected.providerDisplayName +
      (preferredRow && isDemo(preferredRow) ? " (demo)" : "")
    : null;
  const fallbackNames = (decision?.explanation?.fallbackChain ?? [])
    .map((f) => {
      const row = providers.find((p) => p.id === f.providerId);
      const n = row?.name ?? f.displayName;
      return row && isDemo(row) ? `${n} (demo)` : n;
    })
    .filter((n, i, a) => a.indexOf(n) === i)
    .slice(0, 3);

  /* ── render ────────────────────────────────────────────────── */

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <PageHeader
        title="Providers"
        subtitle="Manage inference sources powering your NEXUS routing pool."
      />

      {error && <div className="mb-4"><ErrorState message={error} onRetry={load} /></div>}

      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          {/* ── Summary strip ─────────────────────────────────── */}
          <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-5">
            <Metric label="Connected" value={connected.length} />
            <Metric label="Healthy" value={healthyCount} tone="ok" />
            <Metric label="Free Models" value={pool.freeModels} tone="ok" />
            <Metric label="No-Key Sources" value={noKeySources} />
            <Metric
              label="Needs Attention"
              value={attentionCount}
              tone={attentionCount > 0 ? "danger" : undefined}
            />
          </div>

          {/* ── Free pool + routing insight ───────────────────── */}
          <div className="mb-6 grid gap-3 lg:grid-cols-2">
            <div className="panel p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                  Free Pool
                </h3>
                <Link to="/models?filter=free" className="text-xs text-accent hover:underline">
                  View free models →
                </Link>
              </div>
              <p className="text-sm">
                <span className="text-2xl font-semibold">{pool.freeModels}</span>
                <span className="ml-2 text-canvas-muted">
                  free models across {pool.sources} active source{pool.sources === 1 ? "" : "s"}
                </span>
              </p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                {pool.byCapability.map((c) => (
                  <span key={c.label} className="text-xs text-canvas-muted">
                    {c.label} <span className="font-medium text-canvas-ink">{c.count}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="panel p-4">
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-canvas-muted">
                NEXUS Routing
              </h3>
              {preferred ? (
                <div className="space-y-1.5 text-sm">
                  <p>
                    <span className="text-canvas-muted">Preferred: </span>
                    <span className="font-medium text-accent">{preferred}</span>
                  </p>
                  {fallbackNames.length > 0 && (
                    <p className="text-canvas-muted">
                      Fallback: {fallbackNames.join(" → ")}
                    </p>
                  )}
                  <p className="text-xs text-canvas-muted">
                    Free capacity: <span className="text-accent">Healthy</span> · routed via free-first profile
                  </p>
                </div>
              ) : (
                <p className="text-sm text-canvas-muted">
                  No free capacity available — connect a free or no-key source below.
                </p>
              )}
            </div>
          </div>

          {/* ── Action bar ────────────────────────────────────── */}
          <FilterBar
            query={query}
            onQuery={setQuery}
            placeholder="Search providers — try “vision”, “no key”, “groq”…"
            pills={FILTERS.map((f) => ({
              id: f.id,
              label: f.label,
              count:
                f.id === "all"
                  ? connected.length + availableDefs.length
                  : f.id === "connected"
                    ? connected.length
                    : f.id === "attention"
                      ? attentionCount
                      : connected.filter((p) => filterProviderView(p, f.id)).length +
                        [...(catalog?.managed ?? []), ...(catalog?.anonymous ?? [])]
                          .filter((d) => !connectedTypeIds.has(d.id))
                          .filter((d) => filterDefinition(d, f.id)).length,
            }))}
            activePill={filter}
            onPill={(id) => setFilter(id as ProviderFilter)}
            sort={sort}
            onSort={(s) => setSort(s as ProviderSort)}
            sortOptions={SORTS}
            right={
              <button className="btn-primary" onClick={() => setPickerOpen(true)}>
                + Add Provider
              </button>
            }
          />

          {/* ── Connected ─────────────────────────────────────── */}
          <section className="mb-8">
            <SectionHeader
              title="Connected"
              subtitle="Sources currently available to NEXUS"
              right={
                <span className="text-xs text-canvas-muted">
                  {connected.length} provider{connected.length === 1 ? "" : "s"}
                </span>
              }
            />
            {visibleConnected.length === 0 ? (
              <EmptyState
                title={connected.length === 0 ? "No providers connected" : "No providers match"}
                body={
                  connected.length === 0
                    ? "Connect your first inference source — NEXUS will discover models and capabilities automatically."
                    : "Try another search or remove filters."
                }
                action={
                  connected.length === 0 && (
                    <button className="btn-primary" onClick={() => setPickerOpen(true)}>
                      + Add Provider
                    </button>
                  )
                }
              />
            ) : (
              <div className="space-y-2">
                {visibleConnected.map((p) => (
                  <ConnectedRow
                    key={p.id}
                    provider={p}
                    onOpen={() => setDrawer({ type: "detail", provider: p })}
                  />
                ))}
              </div>
            )}
          </section>

          {/* ── Available catalog ─────────────────────────────── */}
          <section className="mb-8">
            <SectionHeader
              title="Add a Provider"
              subtitle="Choose an inference source to expand your routing pool"
              right={
                <span className="text-xs text-canvas-muted">
                  {availableDefs.length} available
                </span>
              }
            />
            {availableDefs.length === 0 ? (
              <EmptyState
                title="No providers found"
                body="Try another search or remove filters — or connect a custom endpoint below."
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {availableDefs.map((d) => (
                  <CatalogTile
                    key={d.id}
                    def={d}
                    onConnect={() => setDrawer({ type: "managed", def: d })}
                  />
                ))}
                {catalog?.custom && filter !== "nokey" && filter !== "free" && (
                  <CustomTile onConnect={() => setDrawer({ type: "custom" })} />
                )}
              </div>
            )}
          </section>

          {/* ── Developer tools (demo providers, collapsed) ───── */}
          {demoProviders.length > 0 && (
            <section>
              <button
                className="mb-3 flex w-full items-center justify-between rounded-xl border border-dashed border-canvas-border px-4 py-2.5 text-left transition hover:border-canvas-muted"
                onClick={() => setDemoOpen((v) => !v)}
              >
                <span>
                  <span className="text-sm font-medium">Developer tools</span>
                  <span className="ml-2 text-xs text-canvas-muted">
                    {demoProviders.length} demo provider{demoProviders.length === 1 ? "" : "s"} — local mocks, not production capacity
                  </span>
                </span>
                <span className="text-xs text-canvas-muted">{demoOpen ? "Hide ▲" : "Show ▼"}</span>
              </button>
              {demoOpen && (
                <div className="space-y-2">
                  {demoVisible.map((p) => (
                    <ConnectedRow
                      key={p.id}
                      provider={p}
                      demo
                      onOpen={() => setDrawer({ type: "detail", provider: p })}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {/* ── Drawers ───────────────────────────────────────────── */}
      {drawer.type === "detail" && (
        <ProviderDetailDrawer
          provider={drawer.provider}
          onClose={() => setDrawer({ type: "closed" })}
          onChanged={load}
        />
      )}
      {(drawer.type === "managed" || drawer.type === "custom") && (
        <Drawer
          title={
            drawer.type === "custom"
              ? "Connect custom endpoint"
              : `Connect ${drawer.def.name}`
          }
          onClose={() => setDrawer({ type: "closed" })}
          wide
        >
          {drawer.type === "managed" ? (
            <ManagedConnect
              def={drawer.def}
              onDone={async () => {
                await load();
                setDrawer({ type: "closed" });
              }}
            />
          ) : (
            <CustomConnect
              onDone={async () => {
                await load();
                setDrawer({ type: "closed" });
              }}
            />
          )}
        </Drawer>
      )}

      {/* ── Provider picker ───────────────────────────────────── */}
      {pickerOpen && (
        <ProviderPicker
          catalog={catalog}
          connectedTypeIds={connectedTypeIds}
          onPick={(def) => {
            setPickerOpen(false);
            setDrawer(
              def.id === "custom" ? { type: "custom" } : { type: "managed", def },
            );
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

/* ── Small pieces ──────────────────────────────────────────────── */

function CustomTile({ onConnect }: { onConnect: () => void }) {
  return (
    <article className="panel flex flex-col border-dashed p-4 transition hover:border-accent/40">
      <div className="mb-2.5 flex items-start gap-3">
        <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg border border-dashed border-canvas-muted text-lg text-canvas-muted">
          ↗
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold">Custom Endpoint</h3>
          <p className="truncate text-xs text-canvas-muted">Any OpenAI-compatible server</p>
        </div>
      </div>
      <p className="mb-3 text-[11px] text-canvas-muted">
        vLLM · llama.cpp · LM Studio · self-hosted
      </p>
      <button className="btn-ghost mt-auto w-full justify-center" onClick={onConnect}>
        Configure
      </button>
    </article>
  );
}

function PageSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="panel h-[68px]" />
        ))}
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="panel h-[110px]" />
        <div className="panel h-[110px]" />
      </div>
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="panel h-[62px]" />
        ))}
      </div>
    </div>
  );
}

/* ── Provider picker modal ─────────────────────────────────────── */

function ProviderPicker({
  catalog,
  connectedTypeIds,
  onPick,
  onClose,
}: {
  catalog: ProviderCatalogResponse | null;
  connectedTypeIds: Set<string>;
  onPick: (def: ProviderDefinitionDto) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [capFilter, setCapFilter] = useState<string | null>(null);

  const defs = useMemo(() => {
    const list = [...(catalog?.managed ?? []), ...(catalog?.anonymous ?? [])];
    const unique = [...new Map(list.map((d) => [d.id, d])).values()];
    return unique.filter((d) => {
      if (connectedTypeIds.has(d.id)) return false;
      if (!matchesQuery(d, q)) return false;
      if (capFilter === "free") return d.freeAccess?.types?.some((t) => t !== "paid_only" && t !== "unknown" && t !== "retired");
      if (capFilter === "nokey") return isNoKey(d);
      if (capFilter) return d.capabilities.includes(capFilter);
      return true;
    });
  }, [catalog, connectedTypeIds, q, capFilter]);

  const CAP_FILTERS = [
    { id: "free", label: "Free" },
    { id: "nokey", label: "No Key" },
    { id: "chat", label: "Chat" },
    { id: "image_understanding", label: "Vision" },
    { id: "tool_calling", label: "Tools" },
    { id: "embeddings", label: "Embeddings" },
  ];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <button
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="panel relative flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-canvas-border px-5 py-4">
          <h2 className="text-sm font-semibold">Add Provider</h2>
          <button className="btn-ghost !px-2.5 !py-1 text-xs" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="border-b border-canvas-border px-5 py-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search providers…"
            autoFocus
            className="mb-2.5 w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
          />
          <div className="flex flex-wrap gap-1">
            {CAP_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setCapFilter(capFilter === f.id ? null : f.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  capFilter === f.id
                    ? "bg-accent/15 text-accent"
                    : "text-canvas-muted hover:bg-canvas-raised"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {defs.length === 0 ? (
            <EmptyState title="No providers found" body="Try another search or remove filters." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {defs.map((d) => (
                <PickerTile key={d.id} def={d} onPick={() => onPick(d)} />
              ))}
              {catalog?.custom && !q && !capFilter && (
                <PickerTile def={catalog.custom} onPick={() => onPick(catalog.custom)} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PickerTile({
  def,
  onPick,
}: {
  def: ProviderDefinitionDto;
  onPick: () => void;
}) {
  const noKey = isNoKey(def);
  return (
    <button
      onClick={onPick}
      className="panel flex items-center gap-3 p-3.5 text-left transition hover:border-accent/40"
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-white"
        style={{ background: def.branding.accent }}
      >
        {def.branding.letter}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium">{def.name}</span>
        </div>
        <p className="truncate text-[11px] text-canvas-muted">
          {def.kind === "custom"
            ? def.shortDescription || "Custom endpoint"
            : `${noKey ? "No key · anonymous" : "API key"} · ${capChips(def.capabilities, 2).join(" · ") || def.shortDescription}`}
        </p>
      </div>
      <span className="text-xs text-canvas-muted group-hover:text-accent">→</span>
    </button>
  );
}
